// Pure helpers for the PEPPOL invoice toggle in checkout.
// No React, no Redux, no network — just data shaping.

/**
 * Coerce a "blank" billing field to an empty string.
 * PersonalInfo currently writes the literal "null" for empty fields,
 * which we treat as empty here.
 */
export function normalizeField(value) {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (trimmed === "" || trimmed === "null") return "";
  return trimmed;
}

/**
 * Format-only check for EU VAT numbers — does NOT hit VIES (the EU's real
 * VAT validation service is the backend's job). Strips common formatting
 * (spaces, dots, dashes), uppercases, then matches a per-country regex.
 * For countries without a specific pattern, falls back to a lenient
 * minimum-length check so unusual non-EU VATs aren't blocked outright.
 *
 * Designed to reject obvious garbage (too short, no country code, etc.)
 * while accepting any well-formed EU number.
 */
const VAT_PATTERNS = {
  AT: /^ATU\d{8}$/,
  BE: /^BE[01]\d{9}$/,
  BG: /^BG\d{9,10}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[A-HJ-NP-Z0-9]{2}\d{9}$/,
  GB: /^GB(\d{9}|\d{12})$/,
  GR: /^(EL|GR)\d{9}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE\d{7}[A-Z]{1,2}$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/,
};

export function isValidVatNumber(vat) {
  if (vat === null || vat === undefined) return false;
  const normalized = String(vat).replace(/[\s.\-]/g, "").toUpperCase();
  if (normalized.length < 4) return false;
  const country = normalized.slice(0, 2);
  if (!/^[A-Z]{2}$/.test(country)) return false;
  const pattern = VAT_PATTERNS[country];
  if (pattern) return pattern.test(normalized);
  // Unknown country — fall back to permissive minimum length.
  return normalized.length >= 10;
}

/**
 * Validate billing info before allowing the PEPPOL toggle to flip on.
 *
 * Rules:
 *   1. If VAT is non-empty but malformed → reject with invalid_vat.
 *   2. At least one of VAT / companyName / invoicingAddress must be non-empty.
 *   3. If VAT is empty, email must be present and not end with @test.com (CI).
 */
export function validatePeppolBillingInfo({
  vat,
  companyName,
  invoicingAddress,
  email,
} = {}) {
  const v = normalizeField(vat);
  const c = normalizeField(companyName);
  const a = normalizeField(invoicingAddress);
  const e = normalizeField(email);

  if (v && !isValidVatNumber(v)) {
    return { valid: false, reason: "invalid_vat" };
  }

  if (!v && !c && !a) return { valid: false, reason: "missing_all" };

  if (!v) {
    if (!e || e.toLowerCase().endsWith("@test.com")) {
      return { valid: false, reason: "invalid_email" };
    }
  }

  return { valid: true };
}

/**
 * Format a shop's structured address (street/zip/city/country) into a
 * single human-readable line. Skips any missing parts so we never end
 * up with "undefined, 1050 undefined" or trailing commas. Returns null
 * when no meaningful piece is available so the caller can omit the
 * field from the payload entirely.
 */
export function formatShopAddress({ street, zip, city, country } = {}) {
  const cityLine = [zip, city]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean)
    .join(" ");
  const parts = [street, cityLine, country]
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

/**
 * Shape the customer billing fields (and optional contextual fields)
 * into the body expected by POST /api/v1/public_invoices/:secure_token.
 *
 * Optional fields:
 *   - shopAddress     → string (where the payment happened — for the
 *                       Odoo invoice's "Payé … - <method> - <address>"
 *                       line). Use formatShopAddress() to build it.
 *   - woshOrderId     → string (e.g. "BG89989_003" — added to the Odoo
 *                       invoice notes block above the user id).
 *
 * Both are omitted from the payload entirely when falsy, so the
 * backend still receives a clean body during the transition (Rails
 * strong params will just drop unknown fields if it hasn't accepted
 * them yet).
 */
/**
 * A "usable" customer email for sending tickets / PEPPOL fallback:
 * present, plausibly formatted, and not one of the @test.com
 * placeholders the user-creation flow generates.
 */
export function isUsableEmail(email) {
  const e = normalizeField(email).toLowerCase();
  if (!e) return false;
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e)) return false;
  if (e.endsWith("@test.com")) return false;
  return true;
}

export function buildPeppolPayload({
  vat,
  companyName,
  invoicingAddress,
  email,
  shopAddress,
  woshOrderId,
} = {}) {
  const body = {
    company_name: normalizeField(companyName),
    vat: normalizeField(vat),
    email: normalizeField(email),
    invoicing_address: normalizeField(invoicingAddress),
  };
  const shop = normalizeField(shopAddress);
  if (shop) body.shop_address = shop;
  const order = normalizeField(woshOrderId);
  if (order) body.wosh_order_id = order;
  return { public_invoice: body };
}
